import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '@/auth/useAuth';
import {
  getContactSettings,
  updateContactSettings,
  updateReservationNotificationEmail,
  updateWhatsAppNumber,
  getQuotationConfig,
  getPriceDisplaySettings,
  updatePriceDisplaySettings,
  setBasePrice,
  setModelDiscount,
  setModelQuotable,
  updateWarrantySettings,
} from '@/features/operations/api';
import type {
  ContactSettings,
  DeclaredFieldItem,
  PricingModelRow,
  QuotationConfig,
} from '@/features/operations/types';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';

/**
 * Where PhoneTec maintains the commercial configuration used by quotations and future sales.
 *
 * Prices are edited one at a time and saved when the field loses focus; the warranty saves its
 * duration and conditions as one value. There is no draft or publish step: changes take effect
 * as soon as their focused write succeeds.
 *
 * The questionnaire structure is validated product scope and has no controls here. An amount
 * that would wipe out the phone's price asks for confirmation before it is saved.
 *
 * Each section has its own tab (#283), kept in `?tab=` so a link or a reload opens the same one.
 * Every draft still lives in this page: the tabs only decide which section is on screen.
 */

const pricingTabs = [
  { value: 'general', label: 'Configuración general' },
  { value: 'contacto', label: 'Contacto por WhatsApp' },
  { value: 'celulares', label: 'Celulares' },
] as const;

type PricingTab = (typeof pricingTabs)[number]['value'];

/// A missing or unknown `?tab=` opens the first section instead of an empty page.
function readTab(value: string | null): PricingTab {
  return pricingTabs.find((tab) => tab.value === value)?.value ?? 'general';
}

const tabId = (tab: PricingTab) => `pricing-tab-${tab}`;
const panelId = (tab: PricingTab) => `pricing-panel-${tab}`;

/// Amounts as the operator typed them, so a half-finished "1" is not saved on every keystroke.
type Drafts = Record<string, string>;

/// The editable representation of a stored amount: "18500.50". Deliberately not the display
/// format from `money.ts` -- this value goes straight back to the backend as typed.
function formatMinorUnits(amountMinor: number) {
  const units = Math.floor(amountMinor / 100);
  const cents = amountMinor % 100;
  return `${units}.${String(cents).padStart(2, '0')}`;
}

/**
 * The typed amount in minor units, or null when it is not an amount the backend would accept.
 *
 * Reads the digits instead of going through `Number`, for the same reason the backend's
 * `parseDecimalToMinorUnits` does: `Number('19.99') * 100` is `1998.9999999999998`, and money
 * is never allowed to touch a float here. Used only to decide whether to warn before saving,
 * so anything unparseable simply returns null and lets the backend do the refusing.
 */
function parseTypedAmountMinor(value: string): number | null {
  const match = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(value.trim());

  if (!match) {
    return null;
  }

  return Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
}

function formatUpdated(updatedAt: string, updatedBy: { fullName: string } | null) {
  const when = new Date(updatedAt).toLocaleString('es-UY');
  return updatedBy ? `${when} · ${updatedBy.fullName}` : when;
}

/// One column of the grid: an answer that has money attached to it on every model.
type AnswerColumn = {
  key: string;
  fieldLabel: string;
  answerLabel: string;
  optionId?: string;
  tierId?: string;
};

function buildAnswerColumns(fields: DeclaredFieldItem[]): AnswerColumn[] {
  return fields.flatMap((field) => {
    const sortedTiers = [...field.tiers].sort((a, b) => b.minPercent - a.minPercent);

    return [
      ...field.options.map((option) => ({
        key: `option:${option.id}`,
        fieldLabel: field.label,
        answerLabel: option.label,
        optionId: option.id,
      })),
      ...sortedTiers.map((tier, index) => ({
        key: `tier:${tier.id}`,
        fieldLabel: field.label,
        answerLabel:
          index === 0
            ? `${tier.minPercent}% o más`
            : tier.minPercent === 0
              ? `Menos de ${sortedTiers[index - 1].minPercent}%`
              : `${tier.minPercent}% a ${sortedTiers[index - 1].minPercent - 1}%`,
        tierId: tier.id,
      })),
    ];
  });
}

function buildDrafts(config: QuotationConfig): Drafts {
  const drafts: Drafts = {};

  for (const model of config.models) {
    for (const price of model.basePrices) {
      drafts[`base:${model.id}:${price.storageGb}`] = formatMinorUnits(price.amountMinor);
    }

    for (const discount of model.discounts) {
      const target = discount.optionId ? `option:${discount.optionId}` : `tier:${discount.tierId}`;
      drafts[`${target}:${model.id}`] = formatMinorUnits(discount.amountMinor);
    }
  }

  return drafts;
}

/// The lowest price the phone is bought at. A discount that reaches it leaves nothing to
/// quote, which is what the confirmation below is for.
function lowestBasePriceMinor(model: PricingModelRow): number | null {
  if (!model.basePrices.length) {
    return null;
  }

  return Math.min(...model.basePrices.map((price) => price.amountMinor));
}

/**
 * Measured against the fixed questions actually being asked.
 */
function isFullyPriced(model: PricingModelRow, columns: AnswerColumn[]) {
  const hasEveryAmount = columns.every((column) =>
    model.discounts.some((discount) =>
      column.optionId ? discount.optionId === column.optionId : discount.tierId === column.tierId,
    ),
  );

  return model.basePrices.length > 0 && hasEveryAmount;
}

type PendingConfirmation = {
  message: string;
  confirm: () => Promise<void>;
};

export function AdminQuotationConfigPage() {
  const { token } = useAuth();
  const { settings: priceDisplay, setSettings: setGlobalPriceDisplay } = usePriceDisplay();
  const [config, setConfig] = useState<QuotationConfig | null>(null);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [contactSettings, setContactSettings] = useState<ContactSettings | null>(null);
  const [contactTemplateDraft, setContactTemplateDraft] = useState('');
  const [whatsappNumberDraft, setWhatsappNumberDraft] = useState('');
  const [reservationEmailDraft, setReservationEmailDraft] = useState('');
  const [exchangeRateDraft, setExchangeRateDraft] = useState('1');
  const [showPricesInUyuDraft, setShowPricesInUyuDraft] = useState(false);
  const [warrantyDurationDraft, setWarrantyDurationDraft] = useState('');
  const [warrantyConditionsDraft, setWarrantyConditionsDraft] = useState('');
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = readTab(searchParams.get('tab'));
  /// The tab asked for while the current one has unsaved changes, waiting for an answer.
  const [pendingTab, setPendingTab] = useState<PricingTab | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const answerColumns = useMemo(() => (config ? buildAnswerColumns(config.fields) : []), [config]);
  const hasConfiguredWarranty = Boolean(
    config?.settings?.warrantyDurationMonths && config.settings.warrantyConditions,
  );

  const resetWarrantyDrafts = (settings: QuotationConfig['settings']) => {
    setWarrantyDurationDraft(settings?.warrantyDurationMonths?.toString() || '');
    setWarrantyConditionsDraft(settings?.warrantyConditions || '');
  };

  /**
   * `keep` preserves what the operator has typed; `reset` throws it away for what is on file.
   *
   * Saving one cell reloads the whole configuration, and rebuilding every draft from that
   * reload used to overwrite the cell the operator had already moved on to -- type an amount,
   * tab to the next cell, start typing, and the first save's response would revert what was
   * under the cursor. This grid exists to load M x O amounts one after another, so that is the
   * normal way of using it, not an edge case. Only a failed save resets, because there the
   * whole point is to stop showing a value nobody stored.
   */
  const applyConfig = (next: QuotationConfig, drafts: 'reset' | 'keep') => {
    setConfig(next);

    if (drafts === 'reset') {
      setDrafts(buildDrafts(next));
      return;
    }

    // Stored values fill in only the cells with no draft yet -- a column that just appeared,
    // an amount saved for the first time. Anything already on screen wins.
    setDrafts((current) => ({ ...buildDrafts(next), ...current }));
  };

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!token) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const [loaded, contact, display] = await Promise.all([
          getQuotationConfig(token),
          getContactSettings(token),
          getPriceDisplaySettings(),
        ]);

        if (!cancelled) {
          applyConfig(loaded, 'reset');
          resetWarrantyDrafts(loaded.settings);
          setContactSettings(contact);
          setContactTemplateDraft(contact?.whatsappMessageTemplate || '');
          setWhatsappNumberDraft(contact?.whatsappNumber || '');
          setReservationEmailDraft(contact?.reservationNotificationEmail || '');
          setExchangeRateDraft(display.exchangeRate);
          setShowPricesInUyuDraft(display.showPricesInUyu);
          setGlobalPriceDisplay(display);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error instanceof Error ? error.message : 'No pudimos cargar la configuración.',
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [token, setGlobalPriceDisplay]);

  // After every hook, so the hook order never changes between renders. The route already
  // requires an authenticated ADMIN; this only narrows the type.
  if (!token) {
    return null;
  }

  /**
   * Whether a tab holds something typed and not saved. Compared the way each save trims, so a
   * stray space is not a change. Celulares has nothing pending: each cell saves when it loses
   * focus, and clicking another tab takes the focus away before the tab changes.
   */
  const isGeneralDirty =
    exchangeRateDraft.trim() !== priceDisplay.exchangeRate ||
    showPricesInUyuDraft !== priceDisplay.showPricesInUyu ||
    warrantyDurationDraft.trim() !== (config?.settings?.warrantyDurationMonths?.toString() || '') ||
    warrantyConditionsDraft.trim() !== (config?.settings?.warrantyConditions || '');
  const isContactDirty =
    contactTemplateDraft.trim() !== (contactSettings?.whatsappMessageTemplate || '') ||
    whatsappNumberDraft.trim() !== (contactSettings?.whatsappNumber || '') ||
    reservationEmailDraft.trim() !== (contactSettings?.reservationNotificationEmail || '');
  const isActiveTabDirty =
    (activeTab === 'general' && isGeneralDirty) || (activeTab === 'contacto' && isContactDirty);

  /// Replaces the history entry: with one entry per tab, Back would change tab without asking.
  const showTab = (tab: PricingTab) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
    // A message belongs to the section it was about.
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const requestTab = (tab: PricingTab) => {
    if (tab === activeTab) return;

    if (isActiveTabDirty) {
      setPendingTab(tab);
      return;
    }

    showTab(tab);
  };

  /// Back to what is on file, so the tab left behind holds no hidden changes.
  const discardActiveTabDrafts = () => {
    if (activeTab === 'general') {
      setExchangeRateDraft(priceDisplay.exchangeRate);
      setShowPricesInUyuDraft(priceDisplay.showPricesInUyu);
      resetWarrantyDrafts(config?.settings ?? null);
    }

    if (activeTab === 'contacto') {
      setContactTemplateDraft(contactSettings?.whatsappMessageTemplate || '');
      setWhatsappNumberDraft(contactSettings?.whatsappNumber || '');
      setReservationEmailDraft(contactSettings?.reservationNotificationEmail || '');
    }
  };

  const refresh = async (drafts: 'reset' | 'keep' = 'keep') => {
    applyConfig(await getQuotationConfig(token), drafts);
  };

  const run = async (action: () => Promise<void>, successText: string) => {
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await action();
      setSuccessMessage(successText);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'No pudimos guardar los cambios.');
      // Whatever failed did not take effect, so the screen goes back to what is on file
      // instead of leaving the operator looking at a value nobody stored.
      await refresh('reset').catch(() => undefined);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Duration and conditions are one value and therefore leave together. Its failure path
   * reconciles only the singleton settings: reusing `run` would reset every price draft even
   * though this request never touched the pricing grid.
   */
  const handleSaveWarranty = async () => {
    const durationText = warrantyDurationDraft.trim();
    const durationMonths = Number(durationText);
    const conditions = warrantyConditionsDraft.trim();

    if (!/^[1-9]\d*$/.test(durationText) || !Number.isSafeInteger(durationMonths)) {
      setErrorMessage('La duración debe ser un número entero mayor que cero.');
      setSuccessMessage(null);
      return;
    }

    if (!conditions) {
      setErrorMessage('Las condiciones de la garantía son obligatorias.');
      setSuccessMessage(null);
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const saved = await updateWarrantySettings(token, { durationMonths, conditions });
      setConfig((current) => (current ? { ...current, settings: saved } : current));
      resetWarrantyDrafts(saved);
      setSuccessMessage('Garantía guardada.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'No pudimos guardar la garantía.');
      const current = await getQuotationConfig(token).catch(() => undefined);

      if (current) {
        setConfig((loaded) => (loaded ? { ...loaded, settings: current.settings } : current));
        resetWarrantyDrafts(current.settings);
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Not routed through `run`: its failure path calls `refresh('reset')`, which reloads the
   * quotation configuration and throws away every price the operator has typed. A failed save
   * of the message must not cost them unrelated work, so this reloads only what it touched.
   */
  const handleSaveContactTemplate = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const saved = await updateContactSettings(token, contactTemplateDraft.trim());
      setContactSettings(saved);
      setContactTemplateDraft(saved.whatsappMessageTemplate || '');
      setSuccessMessage('Mensaje guardado.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'No pudimos guardar el mensaje.');
      const current = await getContactSettings(token).catch(() => undefined);

      if (current !== undefined) {
        setContactSettings(current);
        setContactTemplateDraft(current?.whatsappMessageTemplate || '');
      }
    } finally {
      setIsSaving(false);
    }
  };

  /** Same isolation as the message: a failed save reloads only the contact settings. */
  const handleSaveWhatsappNumber = async () => {
    if (!token) return;
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const saved = await updateWhatsAppNumber(token, whatsappNumberDraft.trim() || null);
      setContactSettings(saved);
      setWhatsappNumberDraft(saved.whatsappNumber || '');
      setSuccessMessage(
        saved.whatsappNumber
          ? 'Número de WhatsApp guardado. La ficha pública ya muestra el botón de consulta.'
          : 'Número de WhatsApp borrado. La ficha pública deja de mostrar el botón de consulta.',
      );
    } catch {
      // The backend answers "Invalid phone" in English; the panel explains it in its own words.
      setErrorMessage(
        'No pudimos guardar el número. Revisá que sea un celular válido, por ejemplo 099 123 456 o +598 99 123 456.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  /** The mailbox for web reservations (VEN-3), saved on its own like the number. */
  const handleSaveReservationEmail = async () => {
    if (!token) return;
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const saved = await updateReservationNotificationEmail(
        token,
        reservationEmailDraft.trim() || null,
      );
      setContactSettings(saved);
      setReservationEmailDraft(saved.reservationNotificationEmail || '');
      setSuccessMessage(
        saved.reservationNotificationEmail
          ? 'Mail para avisos de reservas guardado.'
          : 'Mail para avisos de reservas borrado. Las reservas web se siguen haciendo, sin aviso interno.',
      );
    } catch {
      setErrorMessage('No pudimos guardar el mail. Revisá que sea un email válido.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePriceDisplay = async () => {
    if (!token) return;
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const saved = await updatePriceDisplaySettings(token, {
        exchangeRate: exchangeRateDraft.trim(),
        showPricesInUyu: showPricesInUyuDraft,
      });
      setGlobalPriceDisplay(saved);
      setExchangeRateDraft(saved.exchangeRate);
      setSuccessMessage('Configuración de moneda guardada.');
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'No pudimos guardar el tipo de cambio.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const saveBasePrice = (model: PricingModelRow, storageGb: number, amount: string) => {
    const stored = model.basePrices.find((price) => price.storageGb === storageGb);
    const trimmed = amount.trim();

    // Nothing typed, or nothing changed. Clearing a cell does not erase the stored amount:
    // there is no delete, and to stop quoting a model there is the switch on its row.
    if (!trimmed || (stored && formatMinorUnits(stored.amountMinor) === trimmed)) {
      return;
    }

    return run(async () => {
      await setBasePrice(token, { phoneModelId: model.id, storageGb, amount: trimmed });
      await refresh();
    }, 'Precio base guardado.');
  };

  const saveDiscount = (model: PricingModelRow, column: AnswerColumn, amount: string) => {
    const stored = model.discounts.find((discount) =>
      column.optionId ? discount.optionId === column.optionId : discount.tierId === column.tierId,
    );
    const trimmed = amount.trim();

    if (!trimmed || (stored && formatMinorUnits(stored.amountMinor) === trimmed)) {
      return;
    }

    const save = async () => {
      await run(async () => {
        await setModelDiscount(token, {
          phoneModelId: model.id,
          optionId: column.optionId,
          tierId: column.tierId,
          amount: trimmed,
        });
        await refresh();
      }, 'Monto guardado.');
    };

    // The typo guard that replaced the draft/publish flow. A discount that reaches the base
    // price leaves nothing to quote, and with live editing it would reach the public form
    // immediately.
    const lowestBasePrice = lowestBasePriceMinor(model);
    const typedMinor = parseTypedAmountMinor(trimmed);

    if (lowestBasePrice !== null && typedMinor !== null && typedMinor >= lowestBasePrice) {
      setPendingConfirmation({
        message:
          `${trimmed} es igual o mayor al precio base más bajo de ${model.brand} ${model.model} ` +
          `(${formatMinorUnits(lowestBasePrice)}). Con este monto el equipo deja de cotizar. ` +
          '¿Lo guardamos igual?',
        confirm: save,
      });
      return;
    }

    return save();
  };

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="CONFIGURACIÓN COMERCIAL"
        title="Cotización automática"
        description="Garantía general, contacto y valores por modelo para la operación comercial."
        badge="Solo administración"
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {isLoading || !config ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Tabs
            value={activeTab}
            onChange={(_event, value: PricingTab) => requestTab(value)}
            aria-label="Secciones de la configuración comercial"
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {pricingTabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                id={tabId(tab.value)}
                aria-controls={panelId(tab.value)}
              />
            ))}
          </Tabs>

          {activeTab === 'general' ? (
            <Box role="tabpanel" id={panelId('general')} aria-labelledby={tabId('general')}>
              <SectionCard
                title="Configuración general"
                description="Valores únicos que PhoneTec define para todos los equipos."
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6">Precios en pesos</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Los montos se guardan en USD. El equivalente en pesos usa este tipo de cambio
                      vigente en todas las pantallas.
                    </Typography>
                  </Box>
                  {priceDisplay.updatedAt ? (
                    <Typography variant="caption" color="text.secondary">
                      Última modificación:{' '}
                      {formatUpdated(priceDisplay.updatedAt, priceDisplay.updatedBy)}
                    </Typography>
                  ) : null}
                  <TextField
                    label="Tipo de cambio (1 USD en UYU)"
                    value={exchangeRateDraft}
                    onChange={(event) => setExchangeRateDraft(event.target.value)}
                    inputProps={{ inputMode: 'decimal' }}
                    helperText="Número positivo, con hasta 6 decimales."
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showPricesInUyuDraft}
                        onChange={(event) => setShowPricesInUyuDraft(event.target.checked)}
                      />
                    }
                    label="Mostrar también el equivalente en pesos"
                  />
                  <Box>
                    <PrimaryButton onClick={handleSavePriceDisplay} disabled={isSaving}>
                      Guardar configuración de moneda
                    </PrimaryButton>
                  </Box>

                  <Box>
                    <Typography variant="h6">Garantía</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duración y condiciones que se aplicarán a las ventas futuras.
                    </Typography>
                  </Box>

                  {hasConfiguredWarranty && config.settings ? (
                    <Typography variant="caption" color="text.secondary">
                      Última modificación de la configuración general:{' '}
                      {formatUpdated(config.settings.updatedAt, config.settings.updatedBy)}
                    </Typography>
                  ) : (
                    <Alert severity="info">
                      Todavía no hay una garantía configurada. PhoneTec debe cargar ambos valores.
                    </Alert>
                  )}

                  <TextField
                    label="Duración (meses)"
                    type="number"
                    value={warrantyDurationDraft}
                    inputProps={{ min: 1, step: 1 }}
                    onChange={(event) => setWarrantyDurationDraft(event.target.value)}
                  />

                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Condiciones de la garantía"
                    value={warrantyConditionsDraft}
                    helperText="Este texto define las condiciones que PhoneTec aplicará a la garantía."
                    onChange={(event) => setWarrantyConditionsDraft(event.target.value)}
                  />

                  <Box>
                    <PrimaryButton onClick={handleSaveWarranty} disabled={isSaving}>
                      Guardar garantía
                    </PrimaryButton>
                  </Box>
                </Stack>
              </SectionCard>
            </Box>
          ) : null}

          {activeTab === 'contacto' ? (
            <Box role="tabpanel" id={panelId('contacto')} aria-labelledby={tabId('contacto')}>
              <SectionCard
                title="Contacto por WhatsApp"
                description="El mensaje con el que el equipo abre la conversación desde el panel, y el número al que los compradores escriben desde la ficha de cada equipo."
              >
                <Stack spacing={2}>
                  {contactSettings ? (
                    <Typography variant="caption" color="text.secondary">
                      Última modificación:{' '}
                      {formatUpdated(contactSettings.updatedAt, contactSettings.updatedBy)}
                    </Typography>
                  ) : null}
                  {!contactSettings?.whatsappMessageTemplate ? (
                    <Alert severity="info">
                      Todavía no hay mensaje configurado. Mientras falte, el panel muestra la acción
                      de WhatsApp deshabilitada.
                    </Alert>
                  ) : null}

                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Mensaje inicial"
                    value={contactTemplateDraft}
                    helperText="Se envía tal cual, sin reemplazos. Viaja en la URL de WhatsApp: no incluyas datos personales del cliente."
                    onChange={(event) => setContactTemplateDraft(event.target.value)}
                  />

                  <Box>
                    <PrimaryButton onClick={handleSaveContactTemplate} disabled={isSaving}>
                      Guardar mensaje
                    </PrimaryButton>
                  </Box>

                  <Divider />

                  {!contactSettings?.whatsappNumber ? (
                    <Alert severity="info">
                      Todavía no hay número de PhoneTec. Mientras falte, la ficha pública no muestra
                      el botón "Consultar por WhatsApp".
                    </Alert>
                  ) : null}

                  <TextField
                    fullWidth
                    type="tel"
                    label="Número de WhatsApp de PhoneTec"
                    value={whatsappNumberDraft}
                    placeholder="099 123 456"
                    helperText="Un único número general, público en la ficha de cada equipo. Si es de Uruguay podés escribirlo sin el +598. Dejalo vacío para quitar el botón."
                    onChange={(event) => setWhatsappNumberDraft(event.target.value)}
                    inputProps={{ inputMode: 'tel' }}
                  />

                  <Box>
                    <PrimaryButton onClick={handleSaveWhatsappNumber} disabled={isSaving}>
                      Guardar número
                    </PrimaryButton>
                  </Box>

                  <Divider />

                  {!contactSettings?.reservationNotificationEmail ? (
                    <Alert severity="info">
                      Todavía no hay mail para avisos de reservas. Las reservas desde la web se
                      hacen igual, pero PhoneTec no recibe el aviso por mail.
                    </Alert>
                  ) : null}

                  <TextField
                    fullWidth
                    type="email"
                    label="Mail para avisos de reservas"
                    value={reservationEmailDraft}
                    placeholder="reservas@phonetec.com.uy"
                    helperText="Recibe un aviso por cada reserva hecha desde la web, con el equipo, la sucursal y el comprador. Es interno: no se muestra en la web."
                    onChange={(event) => setReservationEmailDraft(event.target.value)}
                  />

                  <Box>
                    <PrimaryButton onClick={handleSaveReservationEmail} disabled={isSaving}>
                      Guardar mail
                    </PrimaryButton>
                  </Box>
                </Stack>
              </SectionCard>
            </Box>
          ) : null}

          {activeTab === 'celulares' ? (
            <Box role="tabpanel" id={panelId('celulares')} aria-labelledby={tabId('celulares')}>
              <SectionCard
                title="Celulares"
                description="Precio base y cuánto descuenta cada respuesta, por modelo. Una celda vacía no es cero: es un monto sin cargar, y deja al equipo sin cotizar."
              >
                {config.fields.length === 3 ? null : (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    No se pudieron cargar las tres características fijas. Revisá que la migración de
                    configuración esté aplicada antes de editar montos.
                  </Alert>
                )}
                {config.models.length === 0 ? (
                  <Alert severity="info">
                    No hay modelos en el catálogo todavía. Cargalos antes de ponerles precio.
                  </Alert>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Modelo</TableCell>
                          <TableCell>Precio base</TableCell>
                          {answerColumns.map((column) => (
                            <TableCell key={column.key}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {column.fieldLabel}
                              </Typography>
                              {column.answerLabel}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {config.models.map((model) => (
                          <TableRow key={model.id}>
                            <TableCell sx={{ minWidth: 220 }}>
                              <Stack spacing={0.5}>
                                <Typography variant="body2">
                                  {model.brand} {model.model}
                                </Typography>
                                {isFullyPriced(model, answerColumns) ? null : (
                                  <Chip size="small" color="warning" label="Faltan montos" />
                                )}
                                {/* The phone is named right above, so the visible text says only
                                what the switch does. The name it announces still carries the
                                model, because there is one of these per row. */}
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={model.isQuotable}
                                      inputProps={{
                                        'aria-label': `${model.brand} ${model.model}: cotizable`,
                                      }}
                                      onChange={(event) =>
                                        run(async () => {
                                          await setModelQuotable(
                                            token,
                                            model.id,
                                            event.target.checked,
                                          );
                                          await refresh();
                                        }, 'Modelo actualizado.')
                                      }
                                    />
                                  }
                                  label={<Typography variant="caption">Cotizable</Typography>}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {formatUpdated(model.updatedAt, model.updatedBy)}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ minWidth: 180 }}>
                              <Stack spacing={1}>
                                {model.storageOptions.map((storageGb) => (
                                  <TextField
                                    key={storageGb}
                                    size="small"
                                    // The capacity stays visible, because a model with three of
                                    // them would otherwise show three identical empty inputs. The
                                    // full name is announced instead of drawn: it does not fit in
                                    // the notch of a cell this narrow.
                                    label={`${storageGb} GB`}
                                    inputProps={{
                                      'aria-label': `${model.brand} ${model.model} ${storageGb} GB: precio base`,
                                    }}
                                    placeholder="Sin precio"
                                    value={drafts[`base:${model.id}:${storageGb}`] ?? ''}
                                    onChange={(event) =>
                                      setDrafts((current) => ({
                                        ...current,
                                        [`base:${model.id}:${storageGb}`]: event.target.value,
                                      }))
                                    }
                                    onBlur={(event) =>
                                      saveBasePrice(model, storageGb, event.target.value)
                                    }
                                  />
                                ))}
                              </Stack>
                            </TableCell>

                            {answerColumns.map((column) => (
                              <TableCell key={column.key} sx={{ minWidth: 150 }}>
                                <TextField
                                  size="small"
                                  // Announced, not drawn. The column header already says which
                                  // answer this is, and the row says which phone; spelling both
                                  // out inside every cell was unreadable. The accessible name
                                  // still carries the field, because the same answer label can
                                  // repeat across fields -- "Sin daños" sits under both screen
                                  // and casing, and they are different amounts.
                                  inputProps={{
                                    'aria-label': `${model.brand} ${model.model} — ${column.fieldLabel}: ${column.answerLabel}`,
                                  }}
                                  placeholder="Sin cargar"
                                  value={drafts[`${column.key}:${model.id}`] ?? ''}
                                  onChange={(event) =>
                                    setDrafts((current) => ({
                                      ...current,
                                      [`${column.key}:${model.id}`]: event.target.value,
                                    }))
                                  }
                                  onBlur={(event) =>
                                    saveDiscount(model, column, event.target.value)
                                  }
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </SectionCard>
            </Box>
          ) : null}
        </Stack>
      )}

      <Dialog open={Boolean(pendingTab)} onClose={() => setPendingTab(null)}>
        <DialogTitle>Tenés cambios sin guardar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Lo que cambiaste en esta pestaña todavía no se guardó. Si cambiás de pestaña, se
            descarta y vuelve a lo que está guardado.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTab(null)}>Seguir editando</Button>
          <PrimaryButton
            onClick={() => {
              const tab = pendingTab;
              setPendingTab(null);
              discardActiveTabDrafts();
              if (tab) showTab(tab);
            }}
          >
            Descartar y cambiar
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingConfirmation)} onClose={() => setPendingConfirmation(null)}>
        <DialogTitle>Confirmá el monto</DialogTitle>
        <DialogContent>
          <DialogContentText>{pendingConfirmation?.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingConfirmation(null);
              refresh().catch(() => undefined);
            }}
          >
            Cancelar
          </Button>
          <PrimaryButton
            onClick={() => {
              const pending = pendingConfirmation;
              setPendingConfirmation(null);
              pending?.confirm();
            }}
          >
            Guardar igual
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default AdminQuotationConfigPage;
